import type { PersonImage } from '@sentra/contracts';
import { Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pathOf } from '@/api/client';
import { binaryRoutes } from '@/api/routes';
import { Button } from '@/design/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/design/components/dialog';
import { PersonThumbnail } from '@/features/persons/images/person-thumbnail';

/**
 * A record's photographs: the thumbnail grid, the viewer, and the removal.
 *
 * **The thumbnails come from the batched read and the full photograph from an `<img src>`.** A
 * record's five faces are one request between them (`api/thumbnails.ts`), and a face this terminal
 * drew a moment ago on the register is drawn from memory. The full photograph is fetched by the
 * browser itself: that is what streams it, carries the session cookie and honours its own
 * `Cache-Control: no-store`. Its path comes from the route table rather than from a literal here,
 * so a renamed route is a failing drift test rather than a broken photograph found at a gate.
 *
 * **The FULL image is fetched only when one is opened**, never behind the grid. Only the full
 * fetch writes a `person.image_view` audit row, and one record view fetches up to five thumbnails:
 * a grid of full images would be five chain-serialised audit writes on the busiest read path in the
 * product, for photographs nobody looked at.
 *
 * **Removed rows are shown as history rather than hidden.** Their bytes are gone from the object
 * store, so the row discloses nothing except that a photograph once existed and was taken away,
 * which is the only place that fact is visible.
 *
 * **Removal is OPTIONAL, and its absence is what makes this readable on the record screen.** A
 * record is a view: it shows the faces and opens one, and every change to a person is made on the
 * amendment screen. Passing no `onRemove` renders no control rather than a disabled one, because a
 * disabled destructive control on the busiest screen in the product is a control somebody
 * eventually enables.
 */
export function ImageGallery({
  personId,
  images,
  removing = null,
  onRemove,
}: Readonly<{
  personId: string;
  images: readonly PersonImage[];
  /** The photograph currently being removed, or `null`. Only its own control is disabled. */
  removing?: string | null;
  /** Absent on a read-only gallery, which renders no removal control at all. */
  onRemove?: (imageId: string) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState<PersonImage | null>(null);

  const active = images.filter((image) => image.status === 'active');
  const removed = images.filter((image) => image.status === 'removed');

  if (images.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('person.images.empty')}</p>;
  }

  return (
    <div className="flex flex-col gap-3" data-testid="image-gallery">
      <ul className="flex flex-wrap gap-3">
        {active.map((image) => (
          <li key={image.id} className="flex flex-col items-center gap-1" data-image={image.id}>
            <button
              type="button"
              className="overflow-hidden rounded-md border"
              onClick={() => {
                setOpen(image);
              }}
            >
              <PersonThumbnail
                imageId={image.id}
                alt={t('person.images.thumbnailAlt', { position: image.position + 1 })}
                className="size-24 object-cover"
              />
            </button>
            <span className="text-xs text-muted-foreground">
              {image.capturedVia === 'webcam'
                ? t('person.images.fromWebcam')
                : t('person.images.fromUpload')}
            </span>
            {onRemove !== undefined && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={removing === image.id}
                onClick={() => {
                  onRemove(image.id);
                }}
              >
                <Trash2 aria-hidden />
                {t('person.images.remove')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      {removed.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="images-removed">
          {t('person.images.removedCount', { count: removed.length })}
        </p>
      )}

      <Dialog
        open={open !== null}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(null);
          }
        }}
      >
        <DialogContent closeLabel={t('common.close')} data-testid="image-viewer">
          <DialogHeader>
            <DialogTitle>{t('person.images.viewerTitle')}</DialogTitle>
          </DialogHeader>
          {open !== null && (
            <img
              src={pathOf(binaryRoutes.personImageFull, { id: personId, imageId: open.id })}
              alt={t('person.images.fullAlt', { position: open.position + 1 })}
              className="w-full rounded-md"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
